/**
 * What search engines and AI crawlers read before any JavaScript runs: the head of each public page (title,
 * description, canonical, Open Graph, Twitter), JSON-LD, robots.txt, sitemap.xml and llms.txt. Built from the route
 * registry (siteRoutes.ts) and, for offers, from the same public plans DTO the page shows: nothing commercial is
 * written here.
 */
import { SITE_ORIGIN, SITE_ROUTES, SITE_SUMMARY, noindexValue, siteRoute, siteUrl, type SiteMeta } from './siteRoutes.js';

/** The fields of a /api/public/plans plan that the JSON-LD reads. */
export interface SeoPlan {
  key: string;
  name: string;
  currency: string;
  monthlyPriceCents: number;
  includedCredits: number;
  overagePriceCents: number;
}

const ORG_ID = `${SITE_ORIGIN}/#organization`;
const APP_ID = `${SITE_ORIGIN}/#app`;
const SITE_ID = `${SITE_ORIGIN}/#website`;
const OG_IMAGE = `${SITE_ORIGIN}/og.png`;

export function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** JSON inside a <script> data block: no `</script>` or `<!--` in any value can end or break it. */
export function scriptJson(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026');
}

/** A <meta> the site head manages: `name="…"` or `property="…"` (Open Graph). */
export interface HeadMeta {
  attr: 'name' | 'property';
  key: string;
  content: string;
}

/**
 * Everything the site writes into a page's <head>, as data: the server serializes it into the first HTML
 * (siteHeadHtml) and the browser applies it on every navigation and plans revalidation (web pages/site/siteHead.ts),
 * creating, updating and removing tags, so both always describe the page on screen.
 */
export interface SiteHead {
  title: string;
  metas: HeadMeta[];
  canonical: string | null;
  jsonLd: object | null;
}

/** Every <meta> the head manager owns: a key missing from a page's head is removed from the document. */
export const MANAGED_METAS: ReadonlyArray<Pick<HeadMeta, 'attr' | 'key'>> = [
  { attr: 'name', key: 'description' },
  { attr: 'name', key: 'robots' },
  ...['type', 'site_name', 'url', 'title', 'description', 'image', 'image:type', 'image:width', 'image:height', 'image:alt'].map((k) => ({ attr: 'property' as const, key: `og:${k}` })),
  ...['card', 'title', 'description'].map((k) => ({ attr: 'name' as const, key: `twitter:${k}` })),
];

/**
 * The head of a public page (`meta`) or of the not-found page (null: noindex, no canonical, og:url or JSON-LD).
 * `plans`: the well-formed plans the page shows, or null when it shows none (no offers then).
 */
export function siteHead(meta: SiteMeta | null, opts: { indexable?: boolean; plans?: readonly SeoPlan[] | null } = {}): SiteHead {
  // The not-found title is the one the page itself sets in the browser (pages/NotFound.tsx).
  const title = meta?.title ?? 'Not found · Perzo';
  const description = meta?.description ?? 'This page does not exist on www.perzo.art.';
  const url = meta ? siteUrl(meta.path) : null;
  const metas: HeadMeta[] = [{ attr: 'name', key: 'description', content: description }];
  if (!meta || opts.indexable === false) metas.push({ attr: 'name', key: 'robots', content: noindexValue(meta ? siteRoute(meta.path) : null) });
  metas.push({ attr: 'property', key: 'og:type', content: 'website' }, { attr: 'property', key: 'og:site_name', content: 'Perzo' });
  if (url) metas.push({ attr: 'property', key: 'og:url', content: url });
  metas.push(
    { attr: 'property', key: 'og:title', content: title },
    { attr: 'property', key: 'og:description', content: description },
    { attr: 'property', key: 'og:image', content: OG_IMAGE },
    { attr: 'property', key: 'og:image:type', content: 'image/png' },
    { attr: 'property', key: 'og:image:width', content: '1200' },
    { attr: 'property', key: 'og:image:height', content: '630' },
    { attr: 'property', key: 'og:image:alt', content: 'Perzo wordmark: shopper photos, made into the art you sell.' },
    { attr: 'name', key: 'twitter:card', content: 'summary_large_image' },
    { attr: 'name', key: 'twitter:title', content: title },
    { attr: 'name', key: 'twitter:description', content: description },
  );
  const plans = opts.plans?.length ? opts.plans : null;
  return { title, metas, canonical: url, jsonLd: meta ? siteJsonLd(meta, plans) : null };
}

/** The head as HTML for the server's first response. */
export function siteHeadHtml(head: SiteHead): string {
  const tags = [`<title>${escapeHtml(head.title)}</title>`];
  for (const m of head.metas) tags.push(`<meta ${m.attr}="${m.key}" content="${escapeHtml(m.content)}" />`);
  if (head.canonical) tags.push(`<link rel="canonical" href="${escapeHtml(head.canonical)}" />`);
  if (head.jsonLd) tags.push(`<script type="application/ld+json">${scriptJson(head.jsonLd)}</script>`);
  return tags.join('\n    ');
}

const money = (cents: number) => (cents / 100).toFixed(2);

/**
 * Organization, WebSite and SoftwareApplication with stable @ids, plus the page itself. Offers only from the plans
 * the page shows (monthly price and currency); no trial, rating or review is ever stated here.
 */
export function siteJsonLd(meta: SiteMeta, plans: readonly SeoPlan[] | null): object {
  const offers = (plans ?? []).map((plan) => ({
    '@type': 'Offer',
    name: plan.name,
    price: money(plan.monthlyPriceCents),
    priceCurrency: plan.currency,
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: money(plan.monthlyPriceCents),
      priceCurrency: plan.currency,
      billingDuration: 'P1M',
      unitText: 'MONTH',
    },
    url: `${SITE_ORIGIN}/#pricing`,
  }));
  const pageId = `${siteUrl(meta.path)}#webpage`;
  const article = siteRoute(meta.path)?.article;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'Organization', '@id': ORG_ID, name: 'Perzo', url: `${SITE_ORIGIN}/`, email: 'support@perzo.art' },
      { '@type': 'WebSite', '@id': SITE_ID, name: 'Perzo', url: `${SITE_ORIGIN}/`, publisher: { '@id': ORG_ID } },
      {
        '@type': 'SoftwareApplication',
        '@id': APP_ID,
        name: 'Perzo',
        description: SITE_SUMMARY,
        url: `${SITE_ORIGIN}/`,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web (Shopify app)',
        image: OG_IMAGE,
        publisher: { '@id': ORG_ID },
        ...(offers.length ? { offers } : {}),
      },
      {
        '@type': 'WebPage',
        '@id': pageId,
        url: siteUrl(meta.path),
        name: meta.title,
        description: meta.description,
        isPartOf: { '@id': SITE_ID },
        about: { '@id': APP_ID },
      },
      // A guide: the same dates the page shows (registry), written and published by Perzo.
      ...(article
        ? [
            {
              '@type': 'Article',
              '@id': `${siteUrl(meta.path)}#article`,
              headline: meta.title,
              description: meta.description,
              url: siteUrl(meta.path),
              mainEntityOfPage: { '@id': pageId },
              datePublished: article.datePublished,
              dateModified: article.dateModified,
              author: { '@id': ORG_ID },
              publisher: { '@id': ORG_ID },
              image: OG_IMAGE,
              inLanguage: 'en',
            },
          ]
        : []),
    ],
  };
}

/**
 * One group for every crawler (search and AI assistants alike are allowed on the public pages): specific groups
 * would not inherit these rules, so there are none. Disallow only keeps crawlers out of app areas; it is not access
 * control (the server authorizes every request).
 */
export function robotsTxt(): string {
  return [
    '# Public pages are open to every crawler, search and AI (OAI-SearchBot, GPTBot, ClaudeBot, PerplexityBot, Google-Extended…).',
    'User-agent: *',
    'Allow: /',
    'Disallow: /api/',
    'Disallow: /shopify/',
    'Disallow: /app',
    'Disallow: /admin',
    'Disallow: /login',
    'Disallow: /accept-invite',
    'Disallow: /personalize/',
    '',
    `Sitemap: ${SITE_ORIGIN}/sitemap.xml`,
    '',
  ].join('\n');
}

/** The routes the sitemap lists (indexable public pages); IndexNow submits the same URLs. */
const sitemapRoutes = () => SITE_ROUTES.filter((route) => route.indexable);
/** The absolute URLs of sitemap.xml, in its order. */
export function sitemapUrls(): string[] {
  return sitemapRoutes().map((route) => siteUrl(route.path));
}

/**
 * Indexable public pages only. `lastmod` only for articles (their dateModified, kept by hand with the text): for the
 * other pages neither the build date nor any other date available here says when the content last changed (prices
 * change without a build; builds change nothing on most pages).
 */
export function sitemapXml(): string {
  const urls = sitemapRoutes().map((route) => {
    const lastmod = route.article ? `    <lastmod>${escapeHtml(route.article.dateModified)}</lastmod>\n` : '';
    return `  <url>\n    <loc>${escapeHtml(siteUrl(route.path))}</loc>\n${lastmod}  </url>`;
  });
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls.join('\n')}\n</urlset>\n`;
}

/**
 * llms.txt: what Perzo is and its canonical pages. Prices and the launch state stay on the pages (no copy here to go
 * stale: pages whose text depends on the launch state are described neutrally).
 */
export function llmsTxt(): string {
  const pages = SITE_ROUTES.filter((route) => route.indexable).map((route) => `- [${route.label}](${siteUrl(route.path)}): ${route.llmsDescription ?? route.description}`);
  return [
    '# Perzo',
    '',
    `> ${SITE_SUMMARY}`,
    '',
    'Perzo is for Shopify merchants who sell prints, canvases and gifts. Shoppers upload a photo on the product page, see it as art in the store’s style on the product and approve one version before checkout; the order comes with the approved art and a print-ready file. Plans and prices are on the home page (pricing section).',
    '',
    '## Pages',
    '',
    ...pages,
    '',
  ].join('\n');
}
